import React, { useEffect, useState } from 'react';
import { colors } from '../../styles/tokens';
import { IndicadoresService } from '../../services/indicadores.service';
import type { DashboardFiltros, VendedorProductoDetalleResponse, VendedorProductoTalla } from '../../types/indicadores.types';
import { fmtInt } from '../../utils/format';
import { buildModeloTallaSheet, exportXlsx } from '../../utils/exportXlsx';
import ExportButton from '../ExportButton';
import { useEsMovil } from '../../hooks/useEsMovil';

export interface ModeloDetalleModalProps {
  /** `externalUserId` del vendedor cuyo `VendedorDetalleModal` abrió este modal. */
  vendedorId: string;
  /** ICOD (7 díg) del producto sobre cuya fila se hizo clic (`fila-detalle-${icod}` en `VendedorDetalleModal`). */
  icod: string;
  /** Descripción del producto — `VendedorDetalleModal` ya la tiene de la fila que abrió este modal, no se vuelve a pedir. */
  descripcion?: string;
  /** Nombre del vendedor — mismo dato que ya trae `VendedorDetalleModal` (heredado de la card de `Vendedores.tsx`). */
  nombre?: string;
  /**
   * Rango + tienda YA activos en Mi tienda, heredados del `VendedorDetalleModal`
   * que abrió este modal (mismo `filtros` que ese componente ya recibió) — a
   * diferencia del app (que nunca manda `chain`/`branchOffice` porque el
   * server los fuerza para `branch_office`), el admin SÍ los envía: ve una
   * tienda arbitraria.
   */
  filtros: DashboardFiltros;
  onClose: () => void;
}

const boxMessageStyle: React.CSSProperties = {
  padding: '20px 16px',
  textAlign: 'center',
  fontWeight: 600,
  fontSize: 13,
};

const thStyle: React.CSSProperties = {
  padding: '8px 12px',
  textAlign: 'right',
  fontSize: 10.5,
  fontWeight: 700,
  color: colors.texto.quaternary,
  textTransform: 'uppercase',
  letterSpacing: '.03em',
  whiteSpace: 'nowrap',
};

const thTallaStyle: React.CSSProperties = { ...thStyle, textAlign: 'left' };

const tdNumStyle: React.CSSProperties = {
  padding: '9px 12px',
  textAlign: 'right',
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
};

const tdTallaStyle: React.CSSProperties = {
  padding: '9px 12px',
  textAlign: 'left',
  fontWeight: 800,
  color: colors.texto.strong,
};

/** Una fila de la corrida por talla: Talla | Consultó | Surtió | Negó | Vendió. */
const FilaTalla: React.FC<{ t: VendedorProductoTalla }> = ({ t }) => (
  <tr data-testid={`fila-talla-${t.talla}`} style={{ borderBottom: `1px solid ${colors.rowBorder}` }}>
    <td style={tdTallaStyle}>{t.talla}</td>
    <td style={tdNumStyle}>{fmtInt(t.consultas)}</td>
    <td style={{ ...tdNumStyle, color: colors.serie.surtio }}>{fmtInt(t.atendidas)}</td>
    <td style={{ ...tdNumStyle, color: colors.serie.negado }}>{fmtInt(t.negados)}</td>
    <td style={{ ...tdNumStyle, color: colors.brand }}>{fmtInt(t.vendido)}</td>
  </tr>
);

/**
 * Modal ANIDADO por MODELO/talla (Fase D, plan
 * `2026-07-22-detalle-por-modelo-y-export-xlsx-plan` — espejo del app, ver
 * `cdr-sa-app/src/features/indicadores/components/ModeloDetalleModal.tsx`) —
 * se abre al hacer clic en una fila de producto (`fila-detalle-${icod}`)
 * DENTRO de `VendedorDetalleModal`, y muestra la corrida por talla de ESE
 * producto para ESE vendedor: Consultó / Surtió / Negó / Vendió. Copia el
 * shell de `VendedorDetalleModal`/`NegadosDrillModal` (backdrop + Esc + botón
 * de cierre, estados loading/error/vacío), con `zIndex` MAYOR (200 contra
 * 100) porque queda ARRIBA del modal padre, ambos montados a la vez.
 *
 * El modal padre (`VendedorDetalleModal`) guarda el estado de "qué modelo
 * está abierto" LOCALMENTE y, mientras este modal está abierto, SU PROPIO
 * listener de Esc se desactiva (ver el guard en `VendedorDetalleModal`) — así
 * la tecla Esc cierra SOLO el modal anidado (el de más arriba), no ambos de
 * un jalón.
 *
 * A diferencia del app (`useGetVendedorProductoDetalleQuery` de RTK Query),
 * este componente usa el fetch propio del admin
 * (`IndicadoresService.getVendedorProductoDetalle` + `useEffect`/`useState`,
 * mismo patrón que `VendedorDetalleModal`/`NegadosDrillModal` del admin) y
 * manda `chain`/`branchOffice` (vía `filtros`, que ya trae `desde`/`hasta`
 * requeridos porque `DashboardFiltros` los exige) — el admin ve una tienda
 * arbitraria, a diferencia del app.
 */
const ModeloDetalleModal: React.FC<ModeloDetalleModalProps> = ({
  vendedorId,
  icod,
  descripcion,
  nombre,
  filtros,
  onClose,
}) => {
  const esMovil = useEsMovil();
  const [data, setData] = useState<VendedorProductoDetalleResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);

    IndicadoresService.getVendedorProductoDetalle(vendedorId, icod, filtros)
      .then((resp) => {
        if (!cancelled) {
          setData(resp);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // Deps en campos PRIMITIVOS de `filtros` (no el objeto completo): mismo
    // criterio que `VendedorDetalleModal`/`NegadosDrillModal` — `filtros`
    // llega como un literal `{...}` que el llamador re-crea en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendedorId, icod, filtros.desde, filtros.hasta, filtros.chain, filtros.branchOffice]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const tallas = data?.tallas ?? [];
  const vacio = !loading && !error && tallas.length === 0;

  const titulo = descripcion || icod;
  const rango = `${filtros.desde}–${filtros.hasta}`;
  const subtitulo = `${nombre ? `${nombre} · ` : ''}Corrida por talla · ${rango}`;

  /** Export a Excel de la hoja "Corrida por talla" (mismo `tallas`/`totales` ya cargados, sin re-fetch). */
  function handleExport() {
    const filename = `modelo-${icod}_${filtros.desde}_${filtros.hasta}.xlsx`;
    return exportXlsx(
      [buildModeloTallaSheet(tallas, data?.totales ?? { consultas: 0, atendidas: 0, negados: 0, vendido: 0 })],
      filename,
    );
  }

  return (
    <div
      data-testid="modelo-detalle-backdrop"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20,23,29,.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 200,
        // En telefono 20 px por lado se comen 40 de los 390: el modal se
        // pega mas al borde para dejarle el ancho a la tabla de dentro.
        padding: esMovil ? 10 : 20,
      }}
    >
      <div
        data-testid="modelo-detalle-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: colors.card,
          borderRadius: 16,
          border: `1px solid ${colors.border}`,
          boxShadow: '0 20px 48px -14px rgba(20,28,54,.45)',
          width: '100%',
          maxWidth: 560,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            background: colors.brand,
            color: '#fff',
            padding: esMovil ? '12px 14px' : '14px 20px',
            display: 'flex',
            alignItems: 'center',
            // En telefono el titulo se lleva la primera linea entera y los
            // controles (Excel + x) bajan a la segunda: en una sola fila de
            // 390 px el titulo quedaba en dos o tres caracteres. `wrap` lo
            // consigue sin meter ningun nodo nuevo, asi que en escritorio la
            // barra es exactamente la misma fila de siempre.
            flexWrap: esMovil ? 'wrap' : undefined,
            gap: esMovil ? 8 : 14,
          }}
        >
          <div style={{ flex: esMovil ? '1 1 100%' : 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 16, letterSpacing: '.01em' }}>{titulo}</div>
            <div style={{ fontSize: 11.5, opacity: 0.72, fontWeight: 600, marginTop: 2 }}>{subtitulo}</div>
          </div>
          <ExportButton
            testid="modelo-detalle"
            variant="dark"
            onExport={handleExport}
            disabled={tallas.length === 0}
          />
          <button
            type="button"
            data-testid="modelo-detalle-cerrar"
            onClick={onClose}
            aria-label="Cerrar"
            style={{
              background: 'rgba(255,255,255,.14)',
              border: 'none',
              borderRadius: 8,
              color: '#fff',
              fontSize: 18,
              lineHeight: 1,
              cursor: 'pointer',
              padding: esMovil ? '0 12px' : '4px 9px',
              // Regla 4: 44 px de area tocable en telefono.
              ...(esMovil ? { minHeight: 44, minWidth: 44, fontSize: 22, marginLeft: 'auto' } : null),
            }}
          >
            ×
          </button>
        </div>

        {loading && (
          <div data-testid="modelo-detalle-loading" style={{ ...boxMessageStyle, color: colors.texto.tertiary }}>
            Cargando detalle…
          </div>
        )}

        {!loading && error && (
          <div data-testid="modelo-detalle-error" style={{ ...boxMessageStyle, color: colors.rojo }}>
            No se pudo cargar el detalle. Reintenta.
          </div>
        )}

        {vacio && (
          <div data-testid="modelo-detalle-vacio" style={{ ...boxMessageStyle, color: colors.texto.tertiary }}>
            Sin actividad por talla en el periodo.
          </div>
        )}

        {!loading && !error && !vacio && data && (
          <div style={{ overflow: 'auto', padding: '4px 0 16px' }}>
            <table data-testid="modelo-detalle-tabla" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${colors.rowBorder}` }}>
                  <th style={thTallaStyle}>Talla</th>
                  <th style={thStyle}>Consultó</th>
                  <th style={thStyle}>Surtió</th>
                  <th style={thStyle}>Negó</th>
                  <th style={thStyle}>Vendió</th>
                </tr>
              </thead>
              <tbody>
                {tallas.map((t) => (
                  <FilaTalla key={t.talla} t={t} />
                ))}
                <tr data-testid="fila-talla-totales" style={{ background: colors.panelBg, fontWeight: 800 }}>
                  <td style={tdTallaStyle}>Total</td>
                  <td style={tdNumStyle}>{fmtInt(data.totales.consultas)}</td>
                  <td style={{ ...tdNumStyle, color: colors.serie.surtio }}>{fmtInt(data.totales.atendidas)}</td>
                  <td style={{ ...tdNumStyle, color: colors.serie.negado }}>{fmtInt(data.totales.negados)}</td>
                  <td style={{ ...tdNumStyle, color: colors.brand }}>{fmtInt(data.totales.vendido)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModeloDetalleModal;
